/* ---------------------------------------------------------------------------
   What the fly-around map is made of.

   Split from the map itself (./flyMap.ts) because everything here is data — a style
   object and a GeoJSON collection — and everything there is a library, a WebGL
   context and a camera nothing in this project can drive without a browser. The
   split is what makes the interesting decisions testable: which sources the map
   reads, how the elevation is encoded, how high a deck is extruded and where its
   corners are. `npm test` asserts all of that; what it cannot assert is that
   MapLibre then draws it.

   NO STYLE URL, NO KEY, NO ACCOUNT. A hosted style would be the usual way to do
   this and every free one comes with a key and a rate limit. This is a style built
   here out of the two tile sources the rest of the app already uses (TILE_SOURCES)
   plus the elevation proxy the 3D view already needs — so the fly-around map shows
   the same ground as the flat map, credited the same way, and adds nothing to the
   deployment's obligations.
   --------------------------------------------------------------------------- */

import type { BridgeView, MapBase } from '../data/types.ts';
import { sourceOf } from './nepalMap.ts';
import { deckFootprint } from './deck.ts';
import { DEM_MAX_ZOOM } from './terrain.ts';

/**
 * The elevation source, as MapLibre reads it.
 *
 * `terrarium` is an encoding MapLibre understands natively, and it happens to be
 * exactly what `/api/terrain` proxies — so the terrain here and the terrain in the
 * 3D view are the same bytes decoded by two different pieces of code. The proxy is
 * same-origin, which is what makes it usable at all: MapLibre reads these tiles
 * into a texture and then samples them, and a cross-origin tile with no CORS
 * header cannot be read any more by a library than by hand.
 */
export const DEM_TILES = ['/api/terrain?z={z}&x={x}&y={y}'];

/** Ids the map's own layers go by, so the runner can address them. */
export const LAYER = {
  base: 'base-raster',
  deckFill: 'deck-extrusion',
} as const;

/**
 * The sky, and it is a property of the STYLE rather than a layer in it.
 *
 * That distinction is the whole of a bug worth remembering. This started life as
 * `{ id: 'sky', type: 'sky', paint: { 'sky-type': 'atmosphere' } }` in the layer
 * list, which is how Mapbox GL JS spells it — MapLibre has never had a layer of
 * that type, and its own list is exactly: fill, line, symbol, circle, heatmap,
 * fill-extrusion, raster, hillshade, color-relief, background.
 *
 * AN INVALID LAYER DOES NOT FAIL BY ITSELF. MapLibre validates the style as a
 * whole and rejects the WHOLE THING, so one unusable layer took the raster
 * imagery, the elevation source and the decks down with it. The map still built:
 * a canvas, a scale bar, zoom and compass controls, a camera at the right place
 * and zoom — everything except a single tile request. It looked like a rendering
 * problem and it was a spelling one.
 *
 * Since 4.7 the sky is set here instead, as six colours and blends on the style
 * root. Daylight, and cool enough not to fight the register's own palette.
 */
export const SKY = {
  'sky-color': '#8fbce8',
  'horizon-color': '#e3ecf6',
  'fog-color': '#dbe5f0',
  'sky-horizon-blend': 0.6,
  'horizon-fog-blend': 0.55,
  'fog-ground-blend': 0.2,
} as const;

/**
 * A whole MapLibre style, as a plain object.
 *
 * Typed loosely on purpose: importing MapLibre's `StyleSpecification` here would
 * pull the library into this module and undo the split described at the top. The
 * shape is checked by the tests instead, which is the level at which a mistake in
 * it actually shows up.
 */
export function flyStyle(base: MapBase): Record<string, unknown> {
  const source = sourceOf(base);
  return {
    version: 8,
    // No glyph or sprite server: nothing in this style draws text or icons, because
    // the labels come baked into the raster tiles and the markers are DOM.
    sources: {
      base: {
        type: 'raster',
        tiles: [...source.tiles],
        tileSize: 256,
        maxzoom: source.maxZoom,
        attribution: source.credit.map((c) => c.text).join(' | '),
      },
      dem: {
        type: 'raster-dem',
        tiles: [...DEM_TILES],
        tileSize: 256,
        // Past this the dataset carries no more detail — see /api/terrain, which
        // refuses deeper requests rather than forwarding them.
        maxzoom: DEM_MAX_ZOOM,
        encoding: 'terrarium',
      },
    },
    /* A horizon rather than a void, for a view whose whole point is being tipped
       over. On the style itself, not in `layers` — see SKY. */
    sky: { ...SKY },
    layers: [{ id: LAYER.base, type: 'raster', source: 'base' }],
  };
}

/** What the terrain is set to once the style has loaded. */
export const TERRAIN = { source: 'dem', exaggeration: 1 } as const;

/**
 * The structures' decks, as extrudable polygons.
 *
 * MapLibre's `fill-extrusion` rather than a three.js custom layer, and that is a
 * deliberate choice rather than the easy one. A custom layer would let the
 * modeller's real geometry stand on this map, but it means building a camera from
 * MapLibre's own projection matrix by hand — about a hundred and fifty lines that
 * either work or produce a bridge somewhere in the Bay of Bengal, with nothing in
 * this project able to tell which until a person looks. `fill-extrusion` is a
 * supported primitive, it reads the terrain under it, and it draws the deck at the
 * register's own recorded length, width and axis. The real geometry already stands
 * on ground in the Site view, which is where it can be checked.
 *
 * `base` and `height` are metres above the terrain surface: the deck sits at its
 * recorded freeboard and is as thick as the span warrants, so what you fly around
 * is a slab at the right size in the right place at a plausible clearance.
 */
export function deckFeatures(list: readonly BridgeView[]): Record<string, unknown> {
  const features = list.flatMap((b) => {
    const corners = deckFootprint(b);
    if (!corners) return [];
    const freeboard = b.freeboard ?? 8;
    /* `deckFootprint` already refused anything with no recorded length, so this
       is reachable only with one — but read defensively rather than asserted,
       because the two are separate functions and the guard is in the other one. */
    const thickness = Math.max(1.2, Math.min(4, (b.length ?? 40) / 40));
    return [
      {
        type: 'Feature',
        id: b.id,
        properties: {
          id: b.id,
          name: b.name,
          base: freeboard,
          height: freeboard + thickness,
        },
        geometry: {
          type: 'Polygon',
          // GeoJSON rings close on themselves; deckFootprint returns four corners.
          coordinates: [[...corners.map(([lng, lat]) => [lng, lat]), [corners[0]![0], corners[0]![1]]]],
        },
      },
    ];
  });

  return { type: 'FeatureCollection', features };
}

/** The extrusion layer, drawn from those features. */
export function deckLayer(): Record<string, unknown> {
  return {
    id: LAYER.deckFill,
    type: 'fill-extrusion',
    source: 'decks',
    paint: {
      'fill-extrusion-color': '#2f6fd0',
      'fill-extrusion-opacity': 0.92,
      // Read per feature, so every structure stands at its own recorded clearance.
      'fill-extrusion-base': ['get', 'base'],
      'fill-extrusion-height': ['get', 'height'],
    },
  };
}
